import Container from "components/layout-components/Container";
import { Modal, Checkbox, Input } from "antd";
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import FirebaseService from "services/FirebaseService";
import { useEffect, useState, useMemo } from "react";
import navigationConfig from "configs/NavigationConfig";
import Scrollbars from 'react-custom-scrollbars';
import { useUserProfiles } from "context/UserProfileContext";
import { useIntl } from "react-intl";

interface ViewItem {
  key: string;
  titleKey: string;
  translatedTitle: string;
}

const SelectViews = () => {
    const { userPerms, userViews, setUserViews } = useUserProfiles();
    const [allItems, setAllItems] = useState<ViewItem[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [editOpen, setEditOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const intl = useIntl();

    useEffect(() => {
        let items: ViewItem[] = [];
        navigationConfig.forEach((menu) => {
            if (menu.key === 'home') return;
            if (menu.submenu.length > 0) {
                menu.submenu.forEach((subMenuFirst) => {
                    items.push({
                        key: subMenuFirst.key,
                        titleKey: subMenuFirst.title,
                        translatedTitle: intl.formatMessage({ id: subMenuFirst.title })
                    });
                })
            } else {
                items.push({
                    key: menu.key,
                    titleKey: menu.title,
                    translatedTitle: intl.formatMessage({ id: menu.title })
                });
            }
        });
        
        // Sort alphabetically by translated title
        items.sort((a, b) => a.translatedTitle.localeCompare(b.translatedTitle));
        
        setAllItems(items);
    }, [intl]);

    const handleOpenViewsEdit = () => {
        // Initialize selected keys based on current userViews
        setSelectedKeys(userViews.filter((v: string) => allItems.some(item => item.key === v)));
        setSearchText(""); // Reset search on open
        setEditOpen(true);
    }

    const handleSaveViews = async () => {
        const newViews = ['home', ...selectedKeys];
        // Ensure we only save views that the user actually has permission for (although UI disables others)
        // logic from previous checks if permissions match, keeping it simple here:
        const toSave = newViews; 
        
        const response = await FirebaseService.setSelfViews(toSave);

        if (!response) return;

        setUserViews(toSave);
        setEditOpen(false);
    }

    const toggleKey = (key: string, checked: boolean) => {
        if (checked) {
            setSelectedKeys(prev => [...prev, key]);
        } else {
            setSelectedKeys(prev => prev.filter(k => k !== key));
        }
    };

    const filteredItems = useMemo(() => {
        if (!searchText) return allItems;
        
        const lowerSearch = searchText.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        
        return allItems.filter(item => {
            const normalizedTitle = item.translatedTitle.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            return normalizedTitle.includes(lowerSearch);
        });
    }, [allItems, searchText]);

    return (
        <>
            <Container>
                <div
                    onClick={handleOpenViewsEdit}
                    style={{
                        padding: '0 1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        opacity: '0.75',
                        marginBottom: '1rem',
                        alignItems: 'center',
                        width: '100%',
                        cursor: 'pointer'
                    }}
                >
                    <p style={{ margin: '0' }}>Filtrar páginas</p>
                    <FilterOutlined />
                </div>
            </Container>
            <Modal
                title="Filtrar páginas"
                visible={editOpen}
                onOk={handleSaveViews}
                onCancel={() => setEditOpen(false)}
            >
                 <div style={{ marginBottom: '1rem' }}>
                    <Input 
                        placeholder="Buscar..." 
                        prefix={<SearchOutlined />} 
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                </div>
                <Scrollbars autoHeight autoHeightMax='15rem'>
                    <div style={{ paddingRight: '1rem' }}>
                        {filteredItems.map((item) => {
                            const isChecked = selectedKeys.includes(item.key);
                            const hasPermission = userPerms.includes(item.key);
                            
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        columnGap: '1rem',
                                        padding: '.5rem 0',
                                        borderBottom: '1px solid #605772',
                                        opacity: isChecked ? '1' : '0.5'
                                    }}
                                    key={item.key}
                                >
                                    <Checkbox
                                        checked={isChecked}
                                        disabled={!hasPermission}
                                        onChange={(e) => toggleKey(item.key, e.target.checked)}
                                    />
                                    {isChecked ?
                                        <span style={{ fontSize: '14px', lineHeight: '26px' }}>{item.translatedTitle}</span> :
                                        <s><span style={{ fontSize: '14px', lineHeight: '26px' }}>{item.translatedTitle}</span></s>
                                    }
                                </div>
                            )
                        })}
                         {filteredItems.length === 0 && (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
                                Nenhuma página encontrada.
                            </div>
                        )}
                    </div>
                </Scrollbars>
            </Modal>
        </>
    )
}

export default SelectViews;